




import "@tuliu/leaflet/dist/leaflet.css";
import {
  defineComponent,
  ref,
  onMounted,
  PropType,
  watchEffect,
  onBeforeUnmount,
} from "@vue/composition-api";
import { eventHub } from "@/utils/eventHub";
import { BaseMap, ZoomLimit, LayerType, GraphicInfo } from "@tuliu/base-map";
import env from "../../../src/config/env";
import { LatLngTuple } from "@tuliu/leaflet";
import { invokeUtilContainerHasSize } from "@/utils/container";

export default defineComponent({
  props: {
    // 地图图层
    layer: {
      type: Number as PropType<LayerType>,
      default: LayerType.defaultSatellite,
    },
    // 地图中心点
    center: {
      type: (Array as unknown) as PropType<LatLngTuple>,
      default: () => [116.378517, 39.865246],
    },
    // 地图缩放层级
    zoom: {
      type: Number,
      default: ZoomLimit.Max,
    },
    // 图形列表
    graphicInfoList: {
      type: Array as PropType<GraphicInfo[]>,
      default: () => [],
    },
    // 只读
    onlyRead: {
      type: Boolean,
      default: false,
    },
    // 放大层级显示图标
    graphicAsIconZoom: {
      type: Boolean,
      default: false,
    },
    // 定位
    graphicInfoLoc: {
      type: Array as unknown as PropType<LatLngTuple>,
      default: () => [],
    },
  },
  emits: [
    "on-mounted",
    "on-zoom",
    "on-move",
    "on-zoom-end",
    "on-map-click",
    "on-polygon-closed",
    "on-polygon-path-change",
    "on-graphic-length-or-area-update",
    "on-map-polygon-click",
    "on-title-click",
  ],
  setup(props, { emit }) {
    const container = ref<HTMLDivElement | null>(null);
    let baseMap: BaseMap;
    let isInit = false;
    const locGraphicInfo = ref<GraphicInfo | null>(null);
    const initBaseMap = () => {
      baseMap = new BaseMap({
        themeColor: "#0097FF",
        TianDiTuKey: env.TianDiTuKey,
        onlyRead: props.onlyRead,
        container: container.value!,
        zoom: props.zoom,
        center: props.center,
        isShowSideLength: true,
        graphicAsIconZoom: !props.graphicAsIconZoom ? 12 : undefined,
        poiOptions: {
          container: document.getElementById("poi_box")! as HTMLDivElement,
          onPoiChange: (p) => {
            baseMap.setCenter(p);
            eventHub.$emit("map-center-title-handle", p);
          },
          limit: 5,
        },
        onMounted() {
          emit("on-mounted");
          isInit = true;
          if (props.graphicInfoLoc.length) {
            locGraphicInfo.value = {
              id: 9999,
              titleClassName: "location-point",
              title: `<div class="location-point-title"></div>`,
              onlyShowTitle: true,
              isClosed: true,
              length: 0,
              area: 0,
              editorEnable: false,
              path: [props.graphicInfoLoc],
            };
          }
          baseMap?.graphicDiff(props.graphicInfoList);
        },
        onZoom(zoom: number, center: LatLngTuple) {
          emit("on-zoom", zoom, center);
        },
        onMove(center: LatLngTuple) {
          emit("on-move", center);
        },
        onZoomEnd(zoom: number) {
          emit("on-zoom-end", zoom);
        },
        onMapClick(point: LatLngTuple) {
          eventHub.$emit("on-map-click-point", point);
          emit("on-map-click", point);
        },
        onPolygonClosed(id: number) {
          emit("on-polygon-closed", id);
        },
        onPolygonPathChange(id: number, path: LatLngTuple[]) {
          emit("on-polygon-path-change", id, path);
        },
        onGraphicLengthOrAreaUpdate(id: number, length: number, area: number) {
          // 当前定位 图标
          if (id == 9999) return;
          emit("on-graphic-length-or-area-update", id, length, area);
        },
        onMapPolygonClick(id: number) {
          emit("on-map-polygon-click", id);
        },
        onTitleClick(id: number) {
          emit("on-title-click", id);
        },
      });
    };

    onMounted(() => {
      if (container.value !== null) {
        invokeUtilContainerHasSize(() => {
          initBaseMap();
        });
      }
    });

    onBeforeUnmount(() => {
      baseMap?.destroy();
    });

    watchEffect(() => {
      const graphicInfoList = props.graphicInfoList;
      if (isInit) {
        baseMap?.graphicDiff(graphicInfoList);
      }
      if (locGraphicInfo.value) {
        baseMap?.graphicDiff([
          ...graphicInfoList,
          locGraphicInfo.value as GraphicInfo,
        ]);
      } else {
        baseMap?.graphicDiff(graphicInfoList);
      }
    });

    watchEffect(() => {
      const center = props.center;
      if (isInit) {
        baseMap.setCenter(center);
      }
    });

    watchEffect(() => {
      const layer = props.layer;
      if (isInit) {
        baseMap.changeLayerByType(layer);
      }
    });

    watchEffect(() => {
      const zoom = props.zoom;
      if (isInit) {
        baseMap.setZoom(zoom);
      }
    });

    eventHub.$on("map-invalidate-size", () => {
      baseMap.mapInstance.invalidateSize(true);
    });

    return {
      container,
      toGraphicCenter(selectedGraphicId?: number | number[]) {
        baseMap.toGraphicCenter(selectedGraphicId);
      },
      forceUpdateLengthAndAreaInfo() {
        baseMap.forceUpdateLengthAndAreaInfo();
      },
    };
  },
});
